'use client';

import { useState } from 'react';
import { Lock, Eye, EyeOff } from 'lucide-react';
import { AmbientBackground, Brand, ErrorBanner, Field, TextInput, Button, Spinner, LanguageToggle } from '../ui';
import { useLanguage } from '../../contexts/LanguageContext';

export const Login = ({ onLogin }: { onLogin: (token: string) => void }) => {
  const { t } = useLanguage();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      const formData = new URLSearchParams();
      formData.append('username', email);
      formData.append('password', password);

      const response = await fetch('/api/token', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData,
      });

      if (!response.ok) {
        throw new Error(t('login.error'));
      }

      const data = await response.json();
      onLogin(data.access_token);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-screen w-full items-center justify-center overflow-hidden bg-brand-bg font-sans">
      <AmbientBackground />

      <div className="absolute top-6 right-8 z-20">
        <LanguageToggle />
      </div>

      <div className="relative z-10 m-4 w-full max-w-[420px] animate-card-in rounded-[1.25rem] border border-brand-teal/20 bg-white/4 p-9 px-9 backdrop-blur-2xl shadow-[0_0_0_1px_rgba(70,192,186,0.06),0_32px_64px_rgba(0,0,0,0.5),0_0_80px_rgba(70,192,186,0.06)]">
        <div className="mb-8">
          <Brand />
        </div>

        <h1 className="m-0 mb-1.5 text-[1.6rem] font-bold tracking-[-0.02em] text-white">{t('login.welcome')}</h1>
        <p className="m-0 mb-7 text-sm text-white/45">{t('login.subtitle')}</p>

        {error && (
          <div className="mb-5">
            <ErrorBanner>{error}</ErrorBanner>
          </div>
        )}

        <form onSubmit={handleLogin} className="flex flex-col gap-[1.1rem]">
          <Field label={t('login.emailLabel')} htmlFor="tf-email">
            <div className="relative flex items-center">
              <svg
                className="pointer-events-none absolute left-[0.9rem] z-10 text-white/30"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                <polyline points="22,6 12,13 2,6" />
              </svg>
              <TextInput
                id="tf-email"
                type="email"
                className="pl-10"
                placeholder={t('login.emailPlaceholder')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
              />
            </div>
          </Field>

          <Field label={t('login.passwordLabel')} htmlFor="tf-password">
            <div className="relative flex items-center">
              <svg
                className="pointer-events-none absolute left-[0.9rem] z-10 text-white/30"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
              <TextInput
                id="tf-password"
                type={showPassword ? 'text' : 'password'}
                className="pl-10 pr-11"
                placeholder={t('login.passwordPlaceholder')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
              />
              <button
                type="button"
                className="absolute right-2.5 flex items-center justify-center rounded-md p-1.5 text-white/30 transition-colors duration-200 hover:bg-brand-teal/8 hover:text-brand-teal cursor-pointer"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? t('login.hidePassword') : t('login.showPassword')}
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </Field>

          <Button id="tf-login-submit" type="submit" className="mt-1 w-full" disabled={isLoading}>
            {isLoading ? <Spinner /> : <Lock size={16} />}
            {isLoading ? t('login.loggingIn') : t('login.loginButton')}
          </Button>
        </form>

        <div className="mt-7 text-center text-[0.78rem] text-white/20">
          <a
            href="https://techfusion.pt"
            target="_blank"
            rel="noopener noreferrer"
            className="text-brand-teal/50 no-underline transition-colors duration-200 hover:text-brand-teal"
          >
            techfusion.pt
          </a>
        </div>
      </div>
    </div>
  );
};
